// <currency-switcher> Web Component
//
// Phase 2 of CDN caching refactor (memory/cdn_caching_plan.md §2.3). Replaces
// the legacy `setCurrency(code) → location.reload()` flow with a no-reload
// path: writes the cookie + localStorage, dispatches `currency-change` on
// document so all <price-display> elements re-render.
//
// The element is a thin wrapper — the existing Alpine-driven dropdown UI
// (open/close, list of currencies) lives in the SSR markup. This element
// just intercepts clicks on `[data-currency-code]` children and routes them
// through the no-reload path. SSR fallback is the same dropdown as today;
// users with no JS still get the legacy `onclick="setCurrency(...)"` flow.
//
// First-paint sync (memory/currency_chip_fix_plan.md): on cacheable routes
// SSR forces canonical "USD" into the chip + dropdown highlight regardless
// of cookie. We mirror the cookie-resolved currency into the chip + active-
// highlight on DOMContentLoaded (NOT in connectedCallback, which fires
// before child elements are parsed — see plan §11 round-1 evidence).
//
// Usage in template:
//   <currency-switcher>
//     <button data-currency-code="EUR">€ EUR</button>
//     <button data-currency-code="USD">$ USD</button>
//     ...
//   </currency-switcher>

(function () {
    // Read preferred currency: localStorage (writer-fast) then cookie
    // (canonical, survives across browser sessions). decodeURIComponent
    // wrapped in try/catch to survive malformed URI-encoded values.
    function _preferredCurrency() {
        try { const ls = localStorage.getItem('preferred_currency'); if (ls) return ls; } catch (e) {}
        const m = document.cookie.match(/(?:^|;\s*)preferred_currency=([^;]+)/);
        if (!m) return '';
        try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
    }

    // Walk the document so desktop + mobile chips stay in sync regardless
    // of which <currency-switcher> instance triggered the update. Adds
    // .cs-ready which releases the anti-FOUC cloak installed by the inline
    // <head> IIFE in base.html.
    function _updateTriggerLabel(code) {
        document.querySelectorAll('[data-currency-trigger-label]').forEach(function (el) {
            el.textContent = code;
            el.classList.add('cs-ready');
        });
    }

    // Walk ALL <currency-switcher> instances so the desktop dropdown gets
    // the same active-highlight even when the click came from mobile (and
    // vice-versa). Pre-v3 _setCurrency scoped this to `this`, which left
    // the other dropdown's highlight stale — latent bug fixed inline.
    function _updateActiveHighlight(code) {
        document.querySelectorAll('currency-switcher').forEach(function (sw) {
            sw.querySelectorAll('[data-currency-code]').forEach(function (b) {
                const isActive = b.getAttribute('data-currency-code') === code;
                b.classList.toggle('bg-blue-50', isActive);
                b.classList.toggle('font-semibold', isActive);
                b.classList.toggle('text-blue-600', isActive);
            });
        });
    }

    // First-paint sync: runs at DOMContentLoaded when child elements
    // (chip label span + dropdown buttons) are fully parsed and addressable.
    function _firstPaintSync() {
        const cur = _preferredCurrency();
        if (!cur) {
            // No preference => SSR USD is correct; still mark chip ready
            // so the cloak releases on routes where the IIFE installed it.
            document.querySelectorAll('[data-currency-trigger-label]').forEach(function (el) {
                el.classList.add('cs-ready');
            });
            return;
        }
        _updateTriggerLabel(cur);
        _updateActiveHighlight(cur);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', _firstPaintSync);
    } else {
        // Script loaded after parse (unlikely with synchronous <head> load,
        // defensive). Run immediately.
        _firstPaintSync();
    }

    // bfcache restore: re-apply chip + highlight from current cookie.
    // Pre-Cloudflare M1.
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) _firstPaintSync();
    });

    // Cross-tab sync: storage event fires ONLY in OTHER tabs of the same
    // origin (never in the writing tab — that tab's UI updated via the
    // same-tab _setCurrency path). Don't write back to localStorage from
    // this handler — the writing tab already did that. Pre-Cloudflare M5.
    window.addEventListener('storage', function (e) {
        if (e.key !== 'preferred_currency' || !e.newValue) return;
        _updateTriggerLabel(e.newValue);
        _updateActiveHighlight(e.newValue);
        document.dispatchEvent(new CustomEvent('currency-change', {
            bubbles: true,
            detail: { currency: e.newValue },
        }));
    });

    class CurrencySwitcher extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            const self = this;
            // Use event delegation so the listener survives any internal
            // re-render Alpine might do.
            this.addEventListener('click', function (ev) {
                const btn = ev.target.closest('[data-currency-code]');
                if (!btn || !self.contains(btn)) return;
                const code = btn.getAttribute('data-currency-code');
                if (!code) return;
                ev.preventDefault();
                ev.stopPropagation();
                self._setCurrency(code);
            });
        }

        _setCurrency(code) {
            // Persist for SSR (cookie) + fast JS read (localStorage).
            // Pre-Cloudflare L1: Secure only on HTTPS (dev http localhost OK).
            const secure = location.protocol === 'https:' ? ';Secure' : '';
            try {
                document.cookie = 'preferred_currency=' + encodeURIComponent(code) + ';path=/;max-age=31536000;SameSite=Lax' + secure;
            } catch (e) { /* ignore */ }
            try { localStorage.setItem('preferred_currency', code); } catch (e) { /* ignore */ }
            // Notify all <price-display> + any other listeners.
            document.dispatchEvent(new CustomEvent('currency-change', {
                bubbles: true,
                detail: { currency: code },
            }));
            // Update visible state on ALL switcher instances and chips
            // (mobile + desktop stay in sync).
            _updateActiveHighlight(code);
            _updateTriggerLabel(code);
            // Close any open Alpine dropdown by toggling currencyOpen.
            const h = document.querySelector('header');
            if (h && h._x_dataStack && h._x_dataStack[0]) {
                h._x_dataStack[0].currencyOpen = false;
            }
        }
    }

    if (!customElements.get('currency-switcher')) {
        customElements.define('currency-switcher', CurrencySwitcher);
    }
})();
